/**
 * useLogoutTemplate.ts
 */
import AnalyticsInstance from '@shared/utils/analytics';
import CmpUtils from '@shared/utils/cmp';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import initAnonymous from '@shared/actions/login/initAnonymous';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { resetMenuListIndex } from '@shared/store/slices/menu.slice';
import { popRoute } from '@shared/store/slices/router.slice';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Storage, { PILOT_HINT_KEY } from '@shared/utils/storage';

/**
 * Hook to use the logout functionality and display of the logout modal.
 *
 * @returns {Object} Object with the logout functionality and display of the logout modal
 */
export default function useLogoutTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch

  const dispatch = useAppDispatch();

  // Selectors

  const focus = useAppSelector(selectFocus);
  const enabled = useAppSelector(selectMagicRemoteEnabled);

  // State
  const [displaySpinner, setDisplaySpinner] = useState(false);

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const logout = useCallback(() => {
    dispatch(
      setModalParams({
        callback: () => {
          AnalyticsInstance.getOmnitrackManager()?.setLogout();
          Storage.delete(PILOT_HINT_KEY);
          AnalyticsInstance.getOmnitrackManager()?.setCustomHeaders(undefined);
          AnalyticsInstance.destroy();
          if (FlavourConfig.capabilities.cmp) {
            setDisplaySpinner(true);
            CmpUtils.openBanner(() => {
              const { router } = store.getState();
              if (router.route.Page === Pages.LOGOUT) {
                initAnonymous(true);
                setDisplaySpinner(false);
              }
            });
          } else {
            initAnonymous(true);
          }
          dispatch(resetMenuListIndex());
        },
      })
    );
    dispatch(setModal(Modals.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Logout keyboard
  useKeyboard({
    focus: Focus.LOGOUT,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'SELECT':
          logout();
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.LOGOUT));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const buttonFocused = focus === Focus.LOGOUT;
    const menuFocused = focus === Focus.MENU;
    return {
      callbacks: {
        button: {
          callback: logout,
        },
        container: {
          onMouseEnter,
        },
      },
      props: {
        button: {
          displaySpinner,
          focused: !enabled && buttonFocused,
        },
        container: {
          animation: menuFocused ? 'main-content-out' : 'main-content-in',
          force: true,
        },
      },
    };
  }, [enabled, focus, onMouseEnter]);
}
